/**
 * CollabAccounts – CRUD for plattform-kontoer (multi-account per platform).
 * Brukes både inline i Samarbeid-fanen og standalone i innstillinger.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Edit3, X, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const PLATFORMS = ["onlyfans", "fansly", "snapchat", "instagram", "x", "tiktok", "reddit", "bluesky", "other"];

export function formatFollowers(n) {
  const num = Number(n || 0);
  if (num >= 1000) {
    const k = num / 1000;
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return `${num}`;
}

export default function CollabAccounts() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/me/collab-accounts").then((r) => setItems(r.data?.results || [])).catch(() => setItems([]));
  };
  useEffect(load, []);

  const grouped = items.reduce((acc, a) => {
    (acc[a.platform] = acc[a.platform] || []).push(a);
    return acc;
  }, {});

  return (
    <section data-testid="collab-accounts">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{t("collab.accounts_title")}</h2>
        <button onClick={() => setEditing("new")} className="btn-magenta inline-flex items-center gap-1.5"
          data-testid="account-new-btn">
          <Plus className="h-3.5 w-3.5" /> {t("collab.add_account")}
        </button>
      </div>
      <p className="text-sm text-[var(--cream-soft)] mb-6">{t("collab.accounts_subtitle")}</p>

      {items.length === 0 ? (
        <div className="panel-tight p-8 text-center text-[var(--cream-soft)] font-fraunces italic">
          {t("collab.no_accounts")}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([platform, list]) => (
            <div key={platform} className="panel-tight p-4">
              <div className="font-meta text-[11px] tracking-[0.2em] text-[var(--cream-faint)] mb-3">
                {platform.toUpperCase()}
              </div>
              <ul className="space-y-2">
                {list.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 py-1" data-testid={`account-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[var(--cream)] font-meta">
                        @{a.handle}
                        {a.label && <span className="text-[var(--cream-faint)] text-xs ml-2">· {a.label}</span>}
                      </div>
                      <div className="text-xs text-[var(--cream-soft)] inline-flex items-center gap-1 mt-0.5">
                        <Users className="h-3 w-3" /> {formatFollowers(a.follower_count)} {t("collab.followers")}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => setEditing(a)}
                        className="text-[var(--cream-faint)] hover:text-[var(--magenta)] p-1">
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button onClick={async () => {
                        if (!window.confirm(t("collab.confirm_delete_account"))) return;
                        await api.delete(`/me/collab-accounts/${a.id}`);
                        toast.success(t("collab.account_deleted"));
                        load();
                      }} className="text-red-300 hover:text-red-200 p-1">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <AccountEditor
          account={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

function AccountEditor({ account, onClose, onSaved }) {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState(account?.platform || "onlyfans");
  const [handle, setHandle] = useState(account?.handle || "");
  const [follower_count, setFollowerCount] = useState(account?.follower_count ?? 0);
  const [label, setLabel] = useState(account?.label || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!handle.trim()) { toast.error(t("collab.err_handle_required")); return; }
    setSaving(true);
    try {
      const payload = {
        platform,
        handle: handle.trim().replace(/^@+/, ""),
        follower_count: Number(follower_count) || 0,
        label: label.trim() || null,
      };
      if (account) {
        await api.patch(`/me/collab-accounts/${account.id}`, payload);
      } else {
        await api.post("/me/collab-accounts", payload);
      }
      toast.success(t("collab.account_saved"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 pt-24 pb-12 overflow-y-auto"
      onClick={onClose} data-testid="account-editor">
      <div className="max-w-md w-full panel-tight p-6 relative my-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-[var(--cream-faint)] hover:text-[var(--magenta)]">
          <X className="h-4 w-4" />
        </button>
        <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-4">
          {account ? t("collab.edit_account") : t("collab.add_account")}
        </h3>

        <div className="space-y-4">
          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.platform_label")}
            </label>
            <div className="flex flex-wrap gap-1">
              {PLATFORMS.map((p) => (
                <button key={p} onClick={() => setPlatform(p)}
                  className={`px-3 py-1.5 font-meta text-[11px] border ${platform === p ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}>
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.handle_label")}
            </label>
            <input value={handle} onChange={(e) => setHandle(e.target.value)} maxLength={80}
              placeholder={t("collab.handle_placeholder")}
              className="input-editorial w-full" data-testid="account-handle" />
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.followers_label")}
            </label>
            <input type="number" min={0} value={follower_count}
              onChange={(e) => setFollowerCount(e.target.value)}
              className="input-editorial w-full" data-testid="account-followers" />
            <p className="text-xs text-[var(--cream-faint)] mt-1">{t("collab.followers_hint")}</p>
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.account_label_label")}
            </label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40}
              placeholder={t("collab.account_label_placeholder")}
              className="input-editorial w-full" data-testid="account-label-input" />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={save} disabled={saving} className="btn-magenta flex-1 disabled:opacity-40"
            data-testid="account-save">
            {saving ? t("collab.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
